<template>
  <div
    class="progress-header d-flex align-center"
    :class="{
      'progress-header--dense pa-2': variant === 'bar',
      'progress-header--card': variant === 'card'
    }"
    role="button"
    tabindex="0"
    @click="onActivate"
    @keydown.enter.prevent="onActivate"
    @keydown.space.prevent="onActivate"
  >
    <div
      class="progress-header__trophy bg-primary d-flex align-center justify-center flex-shrink-0 me-2"
      :class="{
        'progress-header__trophy--bar': variant === 'bar',
        'progress-header__trophy--card': variant === 'card'
      }"
    >
      <icon
        fill="white"
        :height="variant === 'bar' ? 16 : 18"
        :width="variant === 'bar' ? 16 : 18"
      >
        TrophyNew
      </icon>
    </div>
    <div
      class="progress-header__main flex-grow-1 min-w-0 d-flex flex-column justify-center"
    >
      <div
        v-if="variant !== 'bar'"
        ref="titleEl"
        class="font-weight-medium"
        :class="{
          'progress-header--bar-title': variant === 'bar',
          'progress-header--card-title': variant === 'card'
        }"
        :title="heading"
      >
        {{ heading }}
      </div>

      <v-progress-linear
        v-else
        class="progress-header__bar w-50"
        :model-value="safePercent"
        color="primary"
        height="8"
        style="width: 50% !important;"
        rounded
        rounded-bar
        bg-color="white"
        bg-opacity="0.6"
      />
      <div
        v-if="variant === 'card' && subtitleLine"
        ref="subtitleEl"
        class="text-medium-emphasis progress-header__subtitle"
        :class="{
          'progress-header--bar-subtitle': variant === 'bar',
          'progress-header--card-subtitle': variant === 'card'
        }"
        :title="subtitleLine"
      >
        {{ subtitleLine }}
      </div>
    </div>
    <div
      class="text-body-2 font-weight-bold text-primary flex-shrink-0 text-no-wrap"
      :class="{
        'progress-header--bar-summary ms-2': variant === 'bar',
        'progress-header--card-summary': variant === 'card'
      }"
    >
      {{ summaryValue }}
    </div>
    <v-icon
      v-if="variant === 'card'"
      class="ms-1 text-medium-emphasis progress-header__chevron flex-shrink-0"
      size="22"
      :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
    />
   
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useFitty } from '@/composables/useFitty';

const props = defineProps({
  /** 'card' = experiences strip; 'bar' = app bar chip */
  variant: {
    type: String,
    default: 'card',
    validator: v => ['card', 'bar'].includes(v)
  },
  expanded: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['toggle', 'open-modal']);

const store = useStore();

const config = computed(() => store.state.progressTracker.progressTrackerConfig);
const displayFormat = computed(() => config.value?.display_format ?? 'fraction');

const heading = computed(
  () => config.value?.texts?.prize_name || 'Progress'
);

const subtitleLine = computed(() => config.value?.texts?.subtitle || '');

const titleEl = useFitty({ minSize: 12, maxSize: 15, multiLine: false });
const subtitleEl = useFitty({ minSize: 10, maxSize: 14, multiLine: false });

const fraction = computed(() => store.getters['progressTracker/progressFraction']);
const percent = computed(() => store.getters['progressTracker/progressPercentage']);
const safePercent = computed(() => {
  const n = Number(percent.value);
  if (Number.isNaN(n)) return 0;
  return Math.min(100, Math.max(0, n));
});

const summaryValue = computed(() => {
  if (displayFormat.value === 'percentage') {
    return `${Math.round(Number(percent.value) || 0)}%`;
  }
  return fraction.value;
});

function onActivate() {
  if (props.variant === 'bar') emit('open-modal');
  else emit('toggle');
}
</script>

<style scoped>
.progress-header {
  cursor: pointer;
  user-select: none;
  border-radius: inherit;
}

.progress-header:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.progress-header__trophy {
  border-radius: 50%;
}

.progress-header__trophy--bar {
  width: 26px;
  height: 26px;
}

.progress-header__trophy--card {
  width: 40px;
  height: 40px;
}

.progress-header--card {
  padding: 12px 14px;
}

.progress-header__main {
  min-height: 0;
  row-gap: 1px;
  overflow: hidden;
}

.progress-header__subtitle {
  line-height: 1.15;
  margin-top: 1px;
}

.progress-header__bar {
  min-width: 80px;
}

.progress-header__bar :deep(.v-progress-linear__determinate) {
  transition: width 450ms ease !important;
  border-radius: 999px;
}

.progress-header__bar :deep(.v-progress-linear),
.progress-header__bar :deep(.v-progress-linear__background),
.progress-header__bar :deep(.v-progress-linear__buffer) {
  transition-duration: 450ms !important;
  transition-timing-function: ease !important;
}

.progress-header--dense {
  max-width: 220px;
}

:deep(.progress-header--card-title) {
  font-weight: 600 !important;
  line-height: 1.2rem !important;
  letter-spacing: 0.02em !important;
}

:deep(.progress-header--card-summary) {
  font-size: 1.0625rem !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  line-height: 1.2rem !important;
  margin-left: 8px !important;
  margin-right: 4px !important;
  font-variant-numeric: tabular-nums;
}

:deep(.progress-header--bar-summary) {
  font-variant-numeric: tabular-nums;
}

@media (max-width: 410px) {
  :deep(.progress-header--card-title) {
    line-height: 1rem !important;
  }
  :deep(.progress-header--card-subtitle) {
    line-height: 1rem !important;
  }
  :deep(.progress-header--card-summary){
    font-size: 1rem !important;
    margin-left: 2px !important;
    margin-right: 2px !important;
  }

  .progress-header__bar {
    min-width: 50px !important;
  }
  
}

@media (max-width: 375px) {
  :deep(.progress-header--bar-summary){
    font-size: 0.675rem !important;
  }
  .progress-header__bar {
    min-width: 50px !important;
  }

  :deep(.progress-header--card-title) {
    line-height: 1rem !important;
  }
  :deep(.progress-header--card-subtitle) {
    line-height: 1rem !important;
  }
  :deep(.progress-header--card-summary){
    font-size: 0.875rem !important;
    margin-left: 2px !important;
    margin-right: 2px !important;
  }

  .progress-header__trophy--card {
    width: 28px !important;
    height: 28px !important;
  }

  .progress-header__chevron {
    font-size: 22px !important;
    width: 22px !important;
    height: 22px !important;
  }
}

@media (max-width: 365px) {
  .progress-header__bar {
    min-width: 40px !important;
  }
}

@media (max-width: 355px) {
  .progress-header__trophy--bar {
    width: 22px !important;
    height: 22px !important;
  }
  .progress-header__bar {
    min-width: 30px !important;
  }
  :deep(.progress-header--bar-summary){
    font-size: 0.575rem !important;
    margin-left: 2px !important;
    margin-right: 2px !important;
  }
}

@media (max-width: 328px) {

  :deep(.progress-header--card-title) {
    line-height: 1rem !important;
  }
  :deep(.progress-header--card-subtitle) {
    line-height: 1rem !important;
  }
  :deep(.progress-header--card-summary){
    font-size: 0.875rem !important;
    margin-left: 2px !important;
    margin-right: 2px !important;
  }
}

@media (max-width: 320px) {
  :deep(.progress-header--card-title) {
    line-height: 1rem !important;
  }
  :deep(.progress-header--card-subtitle) {
    line-height: 1rem !important;
  }
  :deep(.progress-header--card-summary){
    font-size: 0.875rem !important;
    margin-left: 2px !important;
    margin-right: 2px !important;
  }
  :deep(.progress-header--bar-summary){
    font-size: 0.625rem !important;
    margin-left: 2px !important;
    margin-right: 2px !important;
  }

  .progress-header__trophy--bar {
    width: 20px !important;
    height: 20px !important;
  }
  .progress-header__bar {
    min-width: 20px !important;
  }
  
}
</style>
